<template>
    <div v-if="isValidControl" :data-control-name="controlName" :class="[control.containerClass, 'control-view-wrapper', control.additionalContainerClass, { 'is-alert-highlight': shouldHighlightAlert, 'is-signature-control': control.type === 'signature' }]">
        <div class="control-view">
            <!-- render the label, readonly should show the label -->
            <ControlLabel
                v-show="control.isShowLabel || readOnly"
                :control="control"
                :read-only="readOnly"
                :view-submission-mode="viewSubmissionMode"
                :language="language"
            />

            <!-- render the exact field -->
            <component
                v-if="!readOnly"
                v-model="valueContainer[controlName]"
                :is="controlComponent"
                :control="control"
                :value-container="valueContainer"
                :class="validationErrorClasses"
                :readOnly="readOnly"
                :viewSubmissionMode="viewSubmissionMode"
                :siteID="siteID"
                :patNum=patNum
                :logoUrl="logoUrl"
                :formSubmissionIDForWeb="formSubmissionIDForWeb"
                :language="language"
            />
            <p
                v-else
                v-text="valueContainer[controlName]"
            />

            <!-- validation error -->
            <template v-if="hasValidationError">
                <div v-for="(mess, i) in validationErrorMessages"
                     :key="i"
                     v-text="mess"
                     :class="styles.FORM.ERROR_MESSAGE"
                ></div>
            </template>
        </div>
    </div>
    <!-- Debug: show warning for invalid controls in dev mode -->
    <div v-else-if="control" class="invalid-control-warning" style="display: none;">
        Unknown control type: {{ control.type }}
    </div>
</template>

<script>
    import {STYLE_INJECTION_MIXIN} from "../../mixins/style-injection-mixin";
    import ControlLabel from "../../views/builder/control-views/ControlLabel";
    import {CONTROLS} from "../../configs/controls";
    import {shouldHighlightAlert} from "../../libraries/alert-highlight";

    export default {
        name: "ControlView",
        components: {ControlLabel},
        mixins: [STYLE_INJECTION_MIXIN],
        props: {
            control: {
                type: Object,
                required: true
            },
            parentId: {
                type: String,
                required: true,
            },
            valueContainer: {
                type: Object,
                required: true
            },
            validationErrors: {
                type: Object,
                default: () => ({}) // empty object
            },
            readOnly: {
              type: Boolean,
              default: false,
            },
            viewSubmissionMode: {
                type: Boolean,
                default: false,
            },
            siteID: { type: String, required: true},
            patNum: { type: Number, required: false},
            logoUrl: {type: String, required: false},
            formSubmissionIDForWeb: { type: String, required: false, default: null},
            language: {
                type: String,
                default: 'en'
            }
        },

        computed: {
            /**
             * Check if control is valid and can be rendered
             */
            isValidControl() {
                return this.control && this.control.type && CONTROLS[this.control.type] && CONTROLS[this.control.type].fieldComponent
            },

            /**
             * This accessor will get the component object to let us inject the right control
             */
            controlComponent() {
                // validate input - return null for invalid controls (they won't render)
                if (!this.isValidControl) {
                    console.warn(`[ControlView] Unknown control type "${this.control?.type}" - control will not render`)
                    return null
                }

                return CONTROLS[this.control.type].fieldComponent
            },

            /**
             * Generate Control Base Name
             * @returns {string}
             */
            controlName() {
                return this.control.name || this.control.uniqueId
            },

            /**
             * Check if current control has validation error(s)
             * @returns {boolean}
             */
            hasValidationError() {
                return !!this.validationErrors[this.controlName]
            },

            /**
             * Short-Path access to the Validation Error MEssages List
             * @returns {String[]}
             */
            validationErrorMessages() {
                return this.validationErrors[this.controlName]
            },

            validationErrorClasses() {
                const classes = {};
                classes[this.styles.FORM.ERROR_OUTLINE] = this.hasValidationError
                return classes
            },

            /**
             * Whether the wrapper should show the red "alert" highlight.
             *
             * The rule itself lives in ../../libraries/alert-highlight.js (import-free, so the
             * node test executes the real file); this computed is only the wiring.
             *
             * Gated on viewSubmissionMode so the highlight only appears for the
             * reviewing audience (office staff on ViewSubmission + the Puppeteer-
             * captured PDF), NOT to the patient while they're filling the form.
             * Both ViewSubmission and ViewSubmissionForPDF set viewSubmissionMode=true;
             * ViewForm (patient-fill) leaves it at the default false.
             *
             * Master switch is control.highlightForAlert; when on:
             *  - For Open Dental Data Fields (opendentaldata — allergies/diseases/medications):
             *    "None" NEVER triggers (same isNoneOption test the backend uses before writing to
             *    OD). Trigger on any other selection, or only on control.alertOptions when the
             *    form author picked specific ones.
             *  - For choice controls (radio/checkbox/dropDown) with per-option isAlert flags:
             *    trigger when the patient's answer matches at least one flagged option (ANY-match).
             *  - For choice controls with NO per-option flags (legacy/simple use):
             *    fall back to "any non-empty answer triggers" so existing forms keep working.
             *  - For non-choice controls (input/date/signature/etc.):
             *    trigger on any non-empty answer.
             */
            shouldHighlightAlert() {
                return shouldHighlightAlert({
                    control: this.control,
                    patientValue: this.valueContainer ? this.valueContainer[this.controlName] : undefined,
                    viewSubmissionMode: this.viewSubmissionMode
                })
            }
        },

    }
</script>

<style scoped>
/* Signatures go full width on tablets, whatever Container Size Class the form stores.
 *
 * Bootstrap's `col-md-*` only collapses to full width BELOW 768px, so a tablet — at or above the
 * md breakpoint (iPad 768px portrait, 1024px landscape) — renders a COL4 signature at 33% (~230px)
 * and a COL7 one at ~60%. Too small to sign in, which is exactly what patients hit in practice.
 * Phones are already fine (below 768px everything is full width) and desktop keeps whatever layout
 * the form author chose, so we only override the md+lg band.
 *
 * Bound as an explicit class rather than `:has(.signature-container)` because Vue 2's scoped-CSS
 * transform gives no guarantee about where it injects the [data-v] attribute inside a :has()
 * argument. Mirrors the unconditional full-width override the PDF export already applies in
 * ViewSubmissionForPDF.vue.
 *
 * Specificity: two classes + the scope attribute beats Bootstrap's single `.col-md-N`, so this
 * wins without !important.
 */
@media (min-width: 768px) and (max-width: 1199.98px) {
    .control-view-wrapper.is-signature-control {
        flex: 0 0 100%;
        max-width: 100%;
        width: 100%;
    }
}

.control-view-wrapper.is-alert-highlight {
    border: 2px solid #DC2626;
    background-color: #FEF2F2;
    border-radius: 6px;
    padding: 10px 12px;
    position: relative;
    margin-top: 14px;
    margin-bottom: 6px;
}

.control-view-wrapper.is-alert-highlight::before {
    content: "\26A0  ALERT";
    position: absolute;
    top: -10px;
    left: 12px;
    background: #DC2626;
    color: #FFFFFF;
    font-size: 10px;
    font-weight: 700;
    padding: 2px 8px;
    border-radius: 3px;
    letter-spacing: 0.5px;
    line-height: 1.4;
    z-index: 1;
}

.control-view-wrapper.is-alert-highlight ::v-deep label,
.control-view-wrapper.is-alert-highlight ::v-deep .control-label-container {
    color: #B91C1C;
    font-weight: 700;
}
</style>
